import React from "react";
import { useLanguage } from "../contexts/LanguageContext";

const HangmanDrawing = ({ wrongGuesses, gameOver, won }) => {
  const { t } = useLanguage();
  const maxWrongGuesses = 6;

  // SVG dimensions
  const width = 300;
  const height = 300;
  const strokeWidth = 4;
  const strokeColor = "#6366f1";

  // Drawing coordinates
  const gallows = {
    base: { x: 50, y: 280, width: 200 },
    pole: { x: 150, y: 280, height: 200 },
    top: { x: 150, y: 80, width: 100 },
    rope: { x: 220, y: 80, height: 30 },
  };

  const person = {
    head: { x: 220, y: 130, radius: 20 },
    body: { x: 220, y: 150, height: 40 },
    leftArm: { x: 220, y: 175, length: 25, angle: -135 },
    rightArm: { x: 220, y: 175, length: 25, angle: -45 },
    leftLeg: { x: 220, y: 190, length: 35, angle: 105 },
    rightLeg: { x: 220, y: 190, length: 35, angle: 75 },
  };

  // Calculate arm and leg positions
  const getArmPosition = (arm) => {
    const angleRad = (arm.angle * Math.PI) / 180;
    const endX = arm.x + Math.cos(angleRad) * arm.length;
    const endY = arm.y + Math.sin(angleRad) * arm.length;
    return { startX: arm.x, startY: arm.y, endX, endY };
  };

  const getLegPosition = (leg) => {
    const angleRad = (leg.angle * Math.PI) / 180;
    const endX = leg.x + Math.cos(angleRad) * leg.length;
    const endY = leg.y + Math.sin(angleRad) * leg.length;
    return { startX: leg.x, startY: leg.y, endX, endY };
  };

  const leftArm = getArmPosition(person.leftArm);
  const rightArm = getArmPosition(person.rightArm);
  const leftLeg = getLegPosition(person.leftLeg);
  const rightLeg = getLegPosition(person.rightLeg);

  // Determine mouth expression state
  // Use Number() to handle any type coercion issues
  const numWrongGuesses = Number(wrongGuesses) || 0;

  // Priority order (check in this order):
  // 1. If wrongGuesses >= 6, ALWAYS show sad (game is lost) - check this FIRST
  // 2. If gameOver && !won, show sad (game lost)
  // 3. If gameOver && won, show happy (victory!)
  // 4. If 1-5 wrong guesses and game not over, show happy (active gameplay)

  // Check if lost: 6+ wrong guesses ALWAYS means lost, OR game over without winning
  const hasReachedMaxGuesses = numWrongGuesses >= 6;
  const isGameLost =
    hasReachedMaxGuesses || (gameOver === true && won !== true);

  // Victory: game over AND won
  const isVictory = gameOver === true && won === true;

  // Show happy: victory OR active gameplay (1-5 wrong guesses, game not over)
  // IMPORTANT: Only show happy if NOT lost (this prevents happy showing on loss)
  const shouldShowHappy =
    !isGameLost &&
    (isVictory ||
      (numWrongGuesses > 0 && numWrongGuesses < 6 && gameOver !== true));

  return (
    <div className="hangman-drawing">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="game-card-svg"
      >
        {/* Background */}
        <rect width={width} height={height} fill="transparent" />

        {/* Gallows - always visible */}
        <g stroke={strokeColor} strokeWidth={strokeWidth} fill="none">
          {/* Base */}
          <line
            x1={gallows.base.x}
            y1={gallows.base.y}
            x2={gallows.base.x + gallows.base.width}
            y2={gallows.base.y}
          />

          {/* Pole */}
          <line
            x1={gallows.pole.x}
            y1={gallows.pole.y}
            x2={gallows.pole.x}
            y2={gallows.pole.y - gallows.pole.height}
          />

          {/* Top */}
          <line
            x1={gallows.pole.x}
            y1={gallows.top.y}
            x2={gallows.top.x + gallows.top.width}
            y2={gallows.top.y}
          />

          {/* Rope */}
          <line
            x1={gallows.rope.x}
            y1={gallows.rope.y}
            x2={gallows.rope.x}
            y2={gallows.rope.y + gallows.rope.height}
          />
        </g>

        {/* Person - drawn progressively based on wrong guesses */}
        <g stroke={strokeColor} strokeWidth={strokeWidth} fill="none">
          {/* Head */}
          {wrongGuesses >= 1 && (
            <circle
              cx={person.head.x}
              cy={person.head.y}
              r={person.head.radius}
            />
          )}

          {/* Body */}
          {wrongGuesses >= 2 && (
            <line
              x1={person.body.x}
              y1={person.body.y}
              x2={person.body.x}
              y2={person.body.y + person.body.height}
            />
          )}

          {/* Left Arm */}
          {wrongGuesses >= 3 && (
            <line
              x1={leftArm.startX}
              y1={leftArm.startY}
              x2={leftArm.endX}
              y2={leftArm.endY}
            />
          )}

          {/* Right Arm */}
          {wrongGuesses >= 4 && (
            <line
              x1={rightArm.startX}
              y1={rightArm.startY}
              x2={rightArm.endX}
              y2={rightArm.endY}
            />
          )}

          {/* Left Leg */}
          {wrongGuesses >= 5 && (
            <line
              x1={leftLeg.startX}
              y1={leftLeg.startY}
              x2={leftLeg.endX}
              y2={leftLeg.endY}
            />
          )}

          {/* Right Leg */}
          {wrongGuesses >= 6 && (
            <line
              x1={rightLeg.startX}
              y1={rightLeg.startY}
              x2={rightLeg.endX}
              y2={rightLeg.endY}
            />
          )}
        </g>

        {/* Face details - only show when head is drawn */}
        {wrongGuesses >= 1 && (
          <g stroke={strokeColor} strokeWidth={2} fill="none">
            {/* Eyes */}
            <circle
              cx={person.head.x - 8}
              cy={person.head.y - 5}
              r={2}
              fill={strokeColor}
            />
            <circle
              cx={person.head.x + 8}
              cy={person.head.y - 5}
              r={2}
              fill={strokeColor}
            />

            {/* Mouth expression */}
            {isGameLost ? (
              /* Sad mouth - game is lost (wrongGuesses >= 6 OR gameOver && !won) */
              <path
                d={`M ${person.head.x - 8} ${person.head.y + 8} Q ${
                  person.head.x
                } ${person.head.y + 12} ${person.head.x + 8} ${
                  person.head.y + 8
                }`}
                stroke={strokeColor}
                strokeWidth={2}
                fill="none"
              />
            ) : shouldShowHappy ? (
              /* Happy mouth - victory OR active gameplay (1-5 wrong guesses, game not over) */
              <path
                d={`M ${person.head.x - 8} ${person.head.y + 12} Q ${
                  person.head.x
                } ${person.head.y + 8} ${person.head.x + 8} ${
                  person.head.y + 12
                }`}
                stroke={strokeColor}
                strokeWidth={2}
                fill="none"
              />
            ) : null}
          </g>
        )}
      </svg>

      {/* Progress indicator */}
      <div className="hangman-progress">
        <div className="progress-text">
          {wrongGuesses === 0 && t("readyToPlay")}
          {wrongGuesses === 1 && t("headDrawn")}
          {wrongGuesses === 2 && t("bodyAdded")}
          {wrongGuesses === 3 && t("leftArm")}
          {wrongGuesses === 4 && t("rightArm")}
          {wrongGuesses === 5 && t("leftLeg")}
          {wrongGuesses === 6 && t("gameOver")}
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(wrongGuesses / maxWrongGuesses) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default HangmanDrawing;
